import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { Observable, of } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { LocalStorage } from '../../../../../shared';
import {
  ExternalPasteDialogResult,
  WKExternalPasteWarningDialogComponent,
} from './wk-external-paste-warning-dialog.component';

/** The subset of a CKEditor instance needed to hand plain text to the 'Paste' plugin. */
export interface PlainTextPasteEditor {
  plugins: { get: (name: string) => unknown };
}

const SKIP_WARNING_VALUE = 'true';

@Injectable({
  providedIn: 'root',
})
export class WkExternalPasteService {
  private static readonly DIALOG_CONFIG: MatDialogConfig = {
    minHeight: 'auto',
    width: '419px',
    panelClass: 'wk-external-paste-warning-dialog',
    autoFocus: false,
  };

  constructor(private readonly dialog: MatDialog) {}

  public shouldSkipWarning(): boolean {
    return (
      localStorage.getItem(LocalStorage.EXTERNAL_PASTE_SKIP_WARNING) ===
      SKIP_WARNING_VALUE
    );
  }

  /**
   * Emits once: true when the external paste may proceed as plain text, false when it
   * is empty or the user exits. Shows the warning dialog unless the user opted out of it.
   */
  public confirmPaste$(plainText: string | undefined): Observable<boolean> {
    if (!plainText?.trim()) {
      return of(false);
    }
    if (this.shouldSkipWarning()) {
      return of(true);
    }
    return this.dialog
      .open<
        WKExternalPasteWarningDialogComponent,
        void,
        ExternalPasteDialogResult
      >(
        WKExternalPasteWarningDialogComponent,
        WkExternalPasteService.DIALOG_CONFIG
      )
      .afterClosed()
      .pipe(
        take(1),
        tap((result) => {
          if (result?.remember) {
            this.rememberSkipWarning();
          }
        }),
        map((result) => Boolean(result))
      );
  }

  public insertPlainText(
    editor: PlainTextPasteEditor | null | undefined,
    plainText: string
  ): void {
    // CKEditor plugin registry is untyped; 'Paste' is a known registered plugin (see ckeditor/plugins/paste/paste.js)
    const paste = editor?.plugins?.get('Paste') as
      | { insertPlainText: (value: string) => void }
      | undefined;
    paste?.insertPlainText(plainText);
  }

  private rememberSkipWarning(): void {
    localStorage.setItem(
      LocalStorage.EXTERNAL_PASTE_SKIP_WARNING,
      SKIP_WARNING_VALUE
    );
  }
}
